import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getPathname } from 'utils/helpers';

export type ChatViewId = 'all' | 'my' | 'notMy' | 'unassigned' | 'bots' | 'custom';
export type ChatViewUsersId =
  | 'allChatUsers'
  | 'myChatUsers'
  | 'notMyChatUsers'
  | 'unassignedChatUsers'
  | 'botsChatUsers'
  | 'customChatUsers';
export const useCurrentChatView: () => {
  id: ChatViewId;
  path: string;
  viewId?: string;
} = () => {
  const { pathname } = useLocation();
  const { viewId } = useParams();
  const relevantPath = React.useMemo(() => getRelevantPath(pathname), [pathname]);
  const view = React.useMemo(() => {
    const view = getCurrentView(relevantPath);
    if (view.id === 'custom' && viewId) {
      return { ...view, viewId };
    }
    return view; // Ensure a default return value
  }, [relevantPath, viewId]);

  return view || { id: 'all', path: '/chats' }; // Provide a fallback value
};

export const getCurrentView: (pathname?: string) => {
  id: ChatViewId;
  path: string;
} = (pathname) => {
  const pathnameReal = pathname || getPathname();
  if (pathnameReal.startsWith('/chats/my')) return { id: 'my', path: '/chats/my' };
  if (pathnameReal.startsWith('/chats/not-my')) return { id: 'notMy', path: '/chats/not-my' };
  if (pathnameReal.startsWith('/chats/unassigned')) return { id: 'unassigned', path: '/chats/unassigned' };
  if (pathnameReal.startsWith('/chats/bots')) return { id: 'bots', path: '/chats/bots' };
  if (pathnameReal.startsWith('/chats/custom')) {
    // Extract viewId from path if it exists: /chats/custom/123 or /chats/custom/123/972...
    const customMatch = pathnameReal.match(/^\/chats\/custom\/([^/]+)/);
    if (customMatch) {
      return { id: 'custom', path: `/chats/custom/${customMatch[1]}` };
    }
    return { id: 'custom', path: '/chats/custom' };
  }

  return { id: 'all', path: '/chats' };
};
export const getCurrentViewPathById = (viewId: ChatViewId): string => {
  switch (viewId) {
    case 'my':
      return '/chats/my';
    case 'notMy':
      return '/chats/not-my';
    case 'unassigned':
      return '/chats/unassigned';
    case 'bots':
      return '/chats/bots';
    case 'custom':
      return '/chats/custom';
    default:
      return '/chats'; // Default to all chats if no specific viewId is provided
  }
};

function getRelevantPath(pathname: string): string {
  // If the path starts with /chats/my, /chats/not-my, /chats/unassigned, or /chats/bots,
  // return this specific segment, ignoring subsequent parts (e.g., chat id).
  // For custom views, we need to preserve the viewId in the path
  const customMatch = pathname.match(/^\/chats\/custom\/([^/]+)/);
  if (customMatch) {
    return `/chats/custom/${customMatch[1]}`;
  }
  
  const match = pathname.match(/^\/chats\/(my|not-my|unassigned|bots|custom)(\/|$)/);
  if (match) {
    return `/chats/${match[1]}`;
  }
  // In all other cases, the view is considered "all"
  return '/chats';
}
